
import { supabase } from "@/integrations/supabase/client";

export interface PerformanceData {
  performanceStage: any[];
  performanceGoals: any[];
  performanceAppraisals: any[];
  performanceBands: any[];
  bandDisagreements: any[];
  bandingSimulation: any[];
  users: any[];
  currentYear: string;
}

export const fetchPerformanceData = async (currentYear: string = 'FY25'): Promise<PerformanceData> => {
  try {
    // Get current user using regular supabase client
    const { data: currentUserData } = await supabase.auth.getUser();
    const currentUserId = currentUserData?.user?.id;

    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    // Get current user details to check if they're a supervisor
    const { data: currentUser } = await supabase
      .from('users')
      .select('*')
      .eq('id', currentUserId)
      .single();

    if (!currentUser) {
      throw new Error('User details not found');
    }

    // Get users under this supervisor's hierarchy - use recursive subordinates for all cases
    let filteredUsers = [];
    const { data: subordinates } = await supabase.rpc('get_user_subordinates', {
      supervisor_uuid: currentUserId
    });
    
    if (subordinates && subordinates.length > 0) {
      const userIds = subordinates.map(s => s.subordinate_id);
      const { data: subordinateUsers } = await supabase
        .from('users')
        .select('id, full_name, email, supervisor_id, company_name, is_unit_head')
        .in('id', userIds);
      
      filteredUsers = subordinateUsers || [];
    }

    const teamMemberIds = filteredUsers.map(user => user.id);

    // Fetch performance management data filtered by year and team members
    const [stageData, goalsData, appraisalsData, bandsData, disagreementsData, simulationData] = await Promise.all([
      supabase.from('performance_stage').select('*').eq('year', currentYear).order('created_at', { ascending: false }),
      supabase.from('performance_goals').select('*').eq('year', currentYear).in('user_id', teamMemberIds).order('created_at', { ascending: false }),
      supabase.from('performance_appraisals').select('*').eq('year', currentYear).in('user_id', teamMemberIds).order('created_at', { ascending: false }),
      supabase.from('performance_bands').select('*').eq('year', currentYear).in('employee_id', teamMemberIds).order('created_at', { ascending: false }),
      supabase.from('band_disagreements').select('*').eq('year', currentYear).in('user_id', teamMemberIds).order('created_at', { ascending: false }),
      supabase.from('banding_simulation').select('*').eq('year', currentYear).order('created_at', { ascending: false })
    ]);

    if (stageData.error) throw stageData.error;
    if (goalsData.error) throw goalsData.error;
    if (appraisalsData.error) throw appraisalsData.error;
    if (bandsData.error) throw bandsData.error;
    if (disagreementsData.error) throw disagreementsData.error;
    if (simulationData.error) throw simulationData.error;

    return {
      performanceStage: stageData.data || [],
      performanceGoals: goalsData.data || [],
      performanceAppraisals: appraisalsData.data || [],
      performanceBands: bandsData.data || [],
      bandDisagreements: disagreementsData.data || [],
      bandingSimulation: simulationData.data || [],
      users: filteredUsers,
      currentYear
    };
  } catch (error) {
    console.error('Error fetching performance data:', error);
    throw error;
  }
};
